import { useMutation } from '@tanstack/react-query';
import { ProFormArchiveModal } from 'components/pro-form-archive-modal';
import { ProFormCreateModal } from 'components/pro-form-create-modal';
import env from 'env';
import { useFormikContext } from 'formik';
import { getCertificate } from 'generated/api-client';
import { useProReviewCertificateAddressesQueryLazyQuery } from 'generated/graphql';
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { equals } from 'remeda';
import { Button, Dropdown, Form, Icon, Modal, Select } from 'semantic-ui-react';
import similarity from 'similarity';
import { ToastError } from 'utils';
import { flattenObject } from 'utils/flatten-object';
import { getFormErrorMessage } from 'utils/form-error-message';
import { formatAddress } from 'utils/format-address';
import { ProFormUpdateFields } from '../pro-form-update-fields-type';

const normalizeAddress = (address: string): string =>
  address
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const areAddressesSimilar = (address: string, other: string): boolean =>
  similarity(normalizeAddress(address), normalizeAddress(other)) >= 0.85;

type CertificateAddress = {
  label: string;
  postalAddressId?: number;
};

type ProActionsDropdownProps = {
  proPresentationId: number;
  proViewId: number;
  companyId?: number;
};

const ProActionsDropdown: FunctionComponent<ProActionsDropdownProps> = ({
  proPresentationId,
  proViewId,
  companyId,
}) => {
  const { values, setFieldValue, validateForm, handleSubmit } =
    useFormikContext<ProFormUpdateFields>();

  const [proFormCreateModalOpen, setProFormCreateModalOpen] = useState(false);
  const [proFormArchiveModalOpen, setProFormArchiveModalOpen] = useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [selectedCertificateAddress, setSelectedCertificateAddress] =
    useState<string>();

  const [certificateAddressesQuery, { data: certificateAddressesData }] =
    useProReviewCertificateAddressesQueryLazyQuery({
      variables: { proPresentationId },
    });

  const { mutate: generateCertificate, isPending: certificateLoading } =
    useMutation({
      mutationFn: async (postalAddressId?: number) =>
        (
          await getCertificate({
            query: { presentationId: proPresentationId, postalAddressId },
            throwOnError: true,
          })
        ).data,
      onError: () =>
        ToastError('Erreur', "Impossible de télécharger l'attestation d'avis"),
    });

  const certificateAddresses = useMemo<CertificateAddress[]>(() => {
    const presentation = certificateAddressesData?.proPresentationByPk;

    if (!presentation) return [];

    const company = presentation.company;

    return [
      ...(company
        ? [
            {
              label: formatAddress({
                street: company.streetAddress,
                postalCode: company.postalCode,
                city: company.city,
              }),
            },
          ]
        : []),
      ...presentation.proViews.flatMap((proView) =>
        proView.proPostalAddress
          ? [
              {
                label: formatAddress(proView.proPostalAddress),
                postalAddressId: proView.id,
              },
            ]
          : [],
      ),
    ].filter(
      (address, index, addresses) =>
        !addresses
          .slice(0, index)
          .some((previous) =>
            areAddressesSimilar(previous.label, address.label),
          ),
    );
  }, [certificateAddressesData]);

  const navigate = useNavigate();

  const onDraft = useCallback(async (): Promise<void> => {
    const errors = await validateForm();
    const errorValues = Object.values(flattenObject(errors)) as string[];
    const errorMsg = getFormErrorMessage(errorValues);

    if (equals(errors, {})) {
      setFieldValue('status', 'draft');
      setTimeout(() => handleSubmit());
    } else {
      ToastError('Impossible de passer la fiche en brouillon !', errorMsg);
    }
  }, [handleSubmit, setFieldValue]);

  const onOpenWebProView = useCallback(() => {
    window.open(
      `${env.SITE_URL}/presentations/${proViewId}-${values.proPresentation.slug}`,
      '_blank',
    );
  }, [proViewId, values.proPresentation.slug]);

  const onProReviewInvitationPdf = useCallback((): void => {
    window.open(
      `${env.API_URL}/form/review-invitation/generate-paper-review-invitation/${proPresentationId}`,
    );
  }, [proViewId]);

  const certificateAddress =
    certificateAddresses.find(
      (address) => address.label === selectedCertificateAddress,
    ) ?? certificateAddresses[0];

  const handleClickReviewCertificate = useCallback((): void => {
    certificateAddressesQuery();
    setCertificateModalOpen(true);
  }, [certificateAddressesQuery]);

  const handleGenerateReviewCertificate = useCallback((): void => {
    generateCertificate(certificateAddress?.postalAddressId, {
      onSuccess: (certificate) => {
        const blob = new Blob([certificate], { type: 'application/pdf' });

        window.open(window.URL.createObjectURL(blob));
        setCertificateModalOpen(false);
      },
    });
  }, [generateCertificate, certificateAddress]);

  return (
    <>
      <Dropdown text="Actions" labeled button floating className="icon">
        <Dropdown.Menu>
          {values.status === 'published' ? (
            <Dropdown.Item
              icon="firstdraft"
              text="Passer en brouillon"
              onClick={onDraft}
            />
          ) : null}
          <Dropdown.Item
            icon="eye"
            text="Voir la fiche"
            onClick={onOpenWebProView}
          />
          <Dropdown.Item
            icon="file pdf"
            text="Formulaire avis papier"
            onClick={onProReviewInvitationPdf}
          />
          <Dropdown.Item
            icon="file pdf"
            text="Attestation d'avis client"
            onClick={handleClickReviewCertificate}
          />
          <Dropdown.Item
            as={Link}
            to={`/pro_review_invitations/create/${proPresentationId}/${proViewId}`}
            icon="plus"
            text="Nouvelle demande d'avis"
          />
          <Dropdown.Item
            as={Link}
            to={`/reviews/create/${proPresentationId}`}
            icon="plus"
            text="Nouvel avis"
          />
          <Dropdown.Item
            icon="plus"
            text="Nouvelle zone"
            onClick={(): void => {
              setProFormCreateModalOpen(true);
            }}
          />
          <Dropdown.Item
            icon="archive"
            text="Archiver"
            onClick={(): void => {
              setProFormArchiveModalOpen(true);
            }}
          />
        </Dropdown.Menu>
      </Dropdown>
      <Modal
        open={certificateModalOpen}
        size="tiny"
        closeOnDimmerClick={false}
        onClose={(): void => setCertificateModalOpen(false)}
      >
        <Modal.Header>Attestation d&apos;avis client</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Adresse affichée sur l&apos;attestation</label>
              <Select
                placeholder="Sélectionnez une adresse..."
                options={certificateAddresses.map((address) => ({
                  key: address.label,
                  text: address.label.toUpperCase(),
                  value: address.label,
                }))}
                value={certificateAddress?.label}
                onChange={(_, { value }): void =>
                  setSelectedCertificateAddress(String(value))
                }
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            type="button"
            onClick={(): void => setCertificateModalOpen(false)}
          >
            Annuler
          </Button>
          <Button
            primary
            type="button"
            loading={certificateLoading}
            disabled={certificateLoading}
            onClick={handleGenerateReviewCertificate}
          >
            <Icon name="file pdf" />
            Générer
          </Button>
        </Modal.Actions>
      </Modal>
      <ProFormArchiveModal
        open={proFormArchiveModalOpen}
        onClose={(): void => {
          setProFormArchiveModalOpen(false);
        }}
        proViewId={proViewId}
        companyId={companyId}
      />
      <ProFormCreateModal
        open={proFormCreateModalOpen}
        onClose={(): void => {
          setProFormCreateModalOpen(false);
        }}
        proPresentationId={proPresentationId}
        onProCreated={(proViewId): void => {
          navigate(`/pros/update/${proViewId}`);
        }}
      />
    </>
  );
};

export default ProActionsDropdown;
